import { Inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable, Subject } from "rxjs";
import { catchError, map, mergeAll, switchMap, tap } from "rxjs/operators";

import { RealTimeConnectOptions, RealTimeMessageEvent } from "./real-time.model";
import { SignalrAction } from "./signalr.action";
import { SignalrConfigService } from "./signalr.config";
import { SIGNALR_CUSTOMER_TOKEN, SIGNALR_GROUP_TOKEN } from "./signalr.provider";
import { SignalrService } from "./signalr.service";

@Injectable()
export class SignalrEffect {
	connectCustomerTransmit$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SignalrAction.connectCustomerTransmit),
				map(({ token }) => this.getCustomerTransmitConfiguration(token)),
				switchMap((config) => this.signalrCustomerService.connect(config)),
				map(() => SignalrAction.connectCustomerTransmitSuccess()),
				catchError(async () => SignalrAction.connectCustomerTransmitFail())
			),
		{ dispatch: true }
	);

	customerTransmitSubscribeToChannel$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SignalrAction.subscribeToCustomerTransmitChannel),
				tap(({ channel }) => {
					const sub$ = this.signalrCustomerService.subscribeToChannel<string>(channel);
					this.addCustomerTransmitChannel(sub$);
				}),
				map(() => undefined)
			),
		{ dispatch: false }
	);

	customerTransmitMessage$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SignalrAction.connectCustomerTransmitSuccess),
			switchMap(() =>
				this.customerTransmitTopics$.pipe(
					map(({ message, channel }) => SignalrAction.customerTransmitChannelMessage(message, channel))
				)
			)
		)
	);

	disconnectCustomerTransmit$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SignalrAction.disconnectCustomerTransmit),
				map(() => this.signalrCustomerService.disconnect())
			),
		{ dispatch: false }
	);

	connectGroupTransmit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SignalrAction.connectGroupTransmit),
			map(() => this.getGroupTransmitConfiguration()),
			switchMap((config) => this.signalrGroupService.connect(config)),
			map(() => SignalrAction.connectGroupTransmitSuccess()),
			catchError(async () => SignalrAction.connectGroupTransmitFail())
		)
	);

	groupTransmitSubscribeToChannel$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SignalrAction.subscribeToGroupTransmitChannel),
				tap(({ topic, group }) => {
					const sub$ = this.signalrGroupService.subscribeToChannel<string>(topic, group);
					this.addGroupTransmitChannel(sub$);
				}),
				map(() => undefined)
			),
		{ dispatch: false }
	);

	groupTransmitMessage$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SignalrAction.connectGroupTransmitSuccess),
			switchMap(() =>
				this.groupTransmitTopics$.pipe(map(({ message }) => SignalrAction.groupTransmitChannelMessage(message)))
			)
		)
	);

	disconnectGroupTransmit$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SignalrAction.disconnectGroupTransmit),
				map(() => this.signalrGroupService.disconnect())
			),
		{ dispatch: false }
	);

	private customerTransmitTopics$: Observable<RealTimeMessageEvent<string>>;
	private groupTransmitTopics$: Observable<RealTimeMessageEvent<string>>;
	private customerTransmitTopicsSubject$: Subject<Observable<RealTimeMessageEvent<string>>> = new Subject();
	private groupTransmitTopicsSubject$: Subject<Observable<RealTimeMessageEvent<string>>> = new Subject();

	constructor(
		private actions$: Actions,
		@Inject(SIGNALR_CUSTOMER_TOKEN) private signalrCustomerService: SignalrService,
		@Inject(SIGNALR_GROUP_TOKEN) private signalrGroupService: SignalrService,
		private config: SignalrConfigService
	) {
		this.customerTransmitTopics$ = this.customerTransmitTopicsSubject$.pipe(mergeAll());
		this.groupTransmitTopics$ = this.groupTransmitTopicsSubject$.pipe(mergeAll());
	}

	private addCustomerTransmitChannel(sub$: Observable<RealTimeMessageEvent<string>>) {
		this.customerTransmitTopicsSubject$.next(sub$);
	}
	private addGroupTransmitChannel(sub$: Observable<RealTimeMessageEvent<string>>) {
		this.groupTransmitTopicsSubject$.next(sub$);
	}

	private getCustomerTransmitConfiguration(token: string): RealTimeConnectOptions {
		return {
			url: `${this.config.getCustomerTransmitSignalRNotificationUrl()}?brandId=${this.config.getApiToken()}&sessionToken=${token}`,
			enableReconnect: true,
		};
	}

	private getGroupTransmitConfiguration(): RealTimeConnectOptions {
		return {
			url: `${this.config.getGroupTransmitSignalRNotificationUrl()}?brandId=${this.config.getApiToken()}`,
			enableReconnect: true,
		};
	}
}
